import axiosApiClient from "..";
import { handleError } from "../../utils/errors";

const BASE_URL = "/users";

class UserService {
  fetchUserInfo = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/me-new`, {
        withCredentials: true,
      });
      if (response.status !== 200) {
        throw new Error();
      }
      const { user, accessToken } = response.data.data;
      return { user, accessToken };
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user's info.");
    }
  };

  addDailyCredits = async () => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/free-credits`);
      if (response.status !== 201) {
        throw new Error();
      }
      const { credits, lastFreeCredits } = response.data.data;
      return { credits, lastFreeCredits };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while clamming daily free credits.",
      );
    }
  };

  submitRequest = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/request`,
        requestData,
      );
      if (response.status !== 201) {
        throw new Error();
      }

      return response.data.data;
    } catch (err) {
      throw handleError(err, "Error occurred while submiting request.");
    }
  };

  spinTheWheel = async () => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/spin-wheel`);
      if (response.status !== 201) {
        throw new Error();
      }
      const { credits, lastFreeCredits, gift } = response.data.data;
      return { credits, lastFreeCredits, gift };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while clamming daily free credits.",
      );
    }
  };

  setUsername = async (body) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/set-username`,
        body,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { affiliateLink } = response.data.data;
      return { username: body.username, affiliateLink };
    } catch (err) {
      throw handleError(err, "Error occurred while setting username.");
    }
  };

  deleteAccount = async ({ username }) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/delete-account`, {
        username,
      });
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting account.");
    }
  };

  getReferralUsers = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/referral-earning`);
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching referral earnings");
    }
  };

  getSameAuthorCollections = async ({ userId }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${userId}`);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching same author collections",
      );
    }
  };

  getUserData = async (userId) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${userId}`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user data.");
    }
  };

  getUserCollectionsData = async ({ username, page = 1 }) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/${username}/collections`,
        {
          params: { page },
        },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user data.");
    }
  };

  getFollowers = async ({ userId, page = 1 }) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/${userId}/followers`,
        { params: { page } },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching followers.");
    }
  };

  getFollowing = async ({ userId, page = 1 }) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/${userId}/following`,
        { params: { page } },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching following.");
    }
  };

  toggleFollow = async (userId) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/toggle-following/${userId}`,
      );
      if (response.status !== 200 && response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while toggling follow.");
    }
  };

  updateProfile = async ({ username, bio, image }) => {
    try {
      const formData = new FormData();
      if (username !== undefined) formData.append("username", username);
      if (bio !== undefined && bio.trim() !== "") formData.append("bio", bio);
      if (image) formData.append("image", image);

      const response = await axiosApiClient.post(`${BASE_URL}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status !== 200 && response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating profile.");
    }
  };
}

const userService = new UserService();

export default userService;
